import React from 'react';
import { Box } from '@mui/material';
import Header6MenuBuilder from './Header6MenuBuilder';
import PagePickerDialog from './PagePickerDialog';
import type { CustomNavMenuKind } from '../utils/customNavMenuTemplates';

interface HeaderInlineNavSettingsProps {
  config: any;
  onHeaderSettingChange: (setting: any, value: any) => void;
  configId?: string;
}

/**
 * Header 6 / 7 ortak üst menü oluşturucusu.
 * Şablon kaydında `inlineNav` açık olan stiller bu bloğu görür.
 */
const HeaderInlineNavSettings: React.FC<HeaderInlineNavSettingsProps> = ({
  config,
  onHeaderSettingChange,
  configId,
}) => {
  const [pagePicker, setPagePicker] = React.useState<{
    open: boolean;
    target: { itemId: string; childId?: string } | null;
  }>({ open: false, target: null });

  const openPagePickerFor = (target: { itemId: string; childId?: string }) =>
    setPagePicker({ open: true, target });

  const closePagePicker = () => setPagePicker({ open: false, target: null });

  const createNavId = () =>
    typeof crypto !== 'undefined' && (crypto as any).randomUUID
      ? (crypto as any).randomUUID()
      : `nav_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  const normalizeNavItems = (items: any[]) =>
    (Array.isArray(items) ? items : [])
      .map((item: any, index: number) => {
        const children = (Array.isArray(item?.children) ? item.children : [])
          .map((child: any, childIndex: number) => ({
            id: String(child?.id || createNavId()),
            label: String(child?.label || ''),
            url: String(child?.url || '/'),
            order: childIndex,
            enabled: child?.enabled !== false,
            openInNewTab: child?.openInNewTab === true,
          }))
          .sort((a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0))
          .map((child: any, childIndex: number) => ({ ...child, order: childIndex }));

        return {
          id: String(item?.id || createNavId()),
          label: String(item?.label || ''),
          url: String(item?.url || '/'),
          order: index,
          enabled: item?.enabled !== false,
          openInNewTab: item?.openInNewTab === true,
          children,
        };
      })
      .sort((a: any, b: any) => Number(a.order ?? 0) - Number(b.order ?? 0))
      .map((item: any, index: number) => ({ ...item, order: index }));

  const updateCustomNavMenu = (updater: (items: any[]) => any[]) => {
    const current = normalizeNavItems(config.headerSettings.customNavMenu || []);
    onHeaderSettingChange('customNavMenu', normalizeNavItems(updater(current)));
  };

  const handlePagePicked = (selection: { url: string; title: string; slug: string }) => {
    const target = pagePicker.target;
    if (!target) {
      closePagePicker();
      return;
    }
    updateCustomNavMenu((current) =>
      current.map((it: any) => {
        if (it.id !== target.itemId) return it;
        if (!target.childId) {
          return { ...it, url: selection.url, label: selection.title, openInNewTab: false };
        }
        return {
          ...it,
          children: (it.children || []).map((ch: any) =>
            ch.id === target.childId
              ? { ...ch, url: selection.url, label: selection.title, openInNewTab: false }
              : ch
          ),
        };
      })
    );
    closePagePicker();
  };

  const sortedMenuItems = normalizeNavItems(config.headerSettings.customNavMenu || []);

  return (
    <Box id="header-section-menu" sx={{ scrollMarginTop: 16 }}>
      <Header6MenuBuilder
        enabled={config.headerSettings.enableCustomNavMenu === true}
        items={sortedMenuItems}
        kind={(config.headerSettings.customNavMenuKind || '') as CustomNavMenuKind | ''}
        configId={configId}
        onEnabledChange={(next) => onHeaderSettingChange('enableCustomNavMenu', next)}
        onKindChange={(nextKind, nextItems) => {
          onHeaderSettingChange('customNavMenuKind', nextKind);
          onHeaderSettingChange('customNavMenu', nextItems);
        }}
        onItemsChange={(nextItems) => onHeaderSettingChange('customNavMenu', nextItems)}
        onOpenPagePicker={openPagePickerFor}
      />

      <PagePickerDialog
        open={pagePicker.open}
        configId={configId || ''}
        onClose={closePagePicker}
        onSelect={handlePagePicked}
      />
    </Box>
  );
};

export default HeaderInlineNavSettings;
